import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLingua } from '../../context/LinguaContext';
import { V2_BASE } from '../config';
import {
  Home, ChevronRight, ShoppingCart, Users2, Tag, ShieldCheck, FileText,
  FileSignature, Trash2, Check, Search, SlidersHorizontal,
  LayoutGrid, MoreVertical, Plus, Package, Lock, Palette, Info,
  List, X, Gauge, Wrench, Ban, Cloud, Layers, Server, Globe,
  ArrowRightLeft, ArrowDownUp, PlusCircle, Pencil, ChevronDown, Download,
} from 'lucide-react';
import { TIPO_CLIENTE, PAESI, fiscalFor } from '../../constants/anagrafica';
import './contabilita.css';
import './account.css';
import './impostazioni.css';

const api = axios.create({ withCredentials: true });
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return '—'; } };
const initialsOf = (n, e) => { if (n) { const p = n.trim().split(/\s+/); return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase(); } return (e?.[0] || 'U').toUpperCase(); };

const useFetch = (url, key, fallback) => {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(url).then(r => { if (alive) setData(key ? (r.data?.[key] ?? fallback) : r.data); })
      .catch(() => { if (alive) setData(fallback); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url, key]); // eslint-disable-line
  return [data, loading];
};

const Toggle = ({ on, onClick }) => (
  <button className={`nfx-toggle${on ? ' is-on' : ''}`} onClick={onClick} aria-pressed={on} type="button" />
);

/* Breadcrumb: home > Parametri > [current] */
const SettCrumb = ({ current }) => {
  const navigate = useNavigate();
  const { t } = useLingua();
  return (
    <div className="nfx-crumb">
      <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
      <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
      {current
        ? <><button className="nfx-crumb__link" onClick={() => navigate(V2_BASE + '/settings')}>{t('Parametri')}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">{t(current)}</span></>
        : <span className="nfx-crumb__cur">{t('Parametri')}</span>}
    </div>
  );
};

/* Pagina "empty"/documenti riutilizzabile */
const DocPage = ({ crumb, title, intro, icon, emptyText, actionLabel }) => (
  <div className="nfx-cb">
    <SettCrumb current={crumb} />
    <h1 className="nfx-cb-title">{title}</h1>
    {intro && <p className="nfx-cb-intro">{intro}</p>}
    <div className="nfx-card">
      <div className="nfx-token-empty">
        <div className="nfx-token-empty__circle">{icon}</div>
        <div className="nfx-token-empty__text">{emptyText}</div>
        {actionLabel && <button className="nfx-btn-blue"><Plus size={16} /> {actionLabel}</button>}
      </div>
    </div>
  </div>
);

/* ═══════════════════ 1. LA TUA ORGANIZZAZIONE ═══════════════════ */
export const OrganizzazionePage = () => {
  const { utente } = useAuth();
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const [profilo] = useFetch('/api/profilo', null, null);
  const [servizi] = useFetch('/api/servizi/riepilogo', 'servizi', []);
  const p = profilo || utente || {};
  const name = p.ragione_sociale || p.nome_completo || [p.nome, p.cognome].filter(Boolean).join(' ') || p.email || '—';
  const initials = initialsOf(p.nome_completo || `${p.nome || ''} ${p.cognome || ''}`, p.email);
  const addr = p.indirizzo || {};
  const via = addr.via || addr.indirizzo || '';
  const citta = [addr.citta || p.citta, (addr.cap || p.cap) ? `(${addr.cap || p.cap})` : ''].filter(Boolean).join(' ');
  const paese = addr.nazione || p.nazione || 'ITALIA';
  const tel = [p.prefisso_telefono, p.telefono].filter(Boolean).join(' ') || '—';
  const nProd = Array.isArray(servizi) ? servizi.length : 0;

  return (
    <div className="nfx-cb">
      <SettCrumb current="La tua organizzazione" />
      <h1 className="nfx-cb-title">La tua organizzazione</h1>
      <div className="nfx-org-grid">
        {/* Left */}
        <div className="nfx-card nfx-org-main">
          <div className="nfx-org-main__head">
            <span className="nfx-org-main__name">{name}</span>
            <button className="nfx-btn-blue" onClick={() => go('/settings/organizzazione/modifica')}>Modificare</button>
          </div>
          <div className="nfx-org-block">
            <div className="nfx-org-block__k">Indirizzo</div>
            {via
              ? <div className="nfx-org-block__v">{via}<br />{citta}<br />{paese}</div>
              : <button className="nfx-org-stat__link" onClick={() => go('/settings/organizzazione/modifica')}>Aggiungere un indirizzo</button>}
          </div>
          <div className="nfx-org-block">
            <div className="nfx-org-block__k">Telefono</div>
            <div className="nfx-org-block__v">{tel}</div>
          </div>
          <div className="nfx-org-links">
            <button className="nfx-org-link" onClick={() => go('/settings/condizioni')}><FileText size={16} /> Condizioni generali</button>
            <button className="nfx-org-link" onClick={() => go('/settings/contratti')}><FileSignature size={16} /> Contratti speciali</button>
            <button className="nfx-org-link nfx-org-link--danger"><Trash2 size={16} /> Elimina l'organizzazione</button>
          </div>
        </div>

        {/* Right */}
        <div className="nfx-org-side">
          <div className="nfx-org-stats">
            <div className="nfx-card nfx-org-stat">
              <span className="nfx-org-stat__ic"><ShoppingCart size={20} /></span>
              <div><div className="nfx-org-stat__n">{nProd} Prodotti</div><button className="nfx-org-stat__link" onClick={() => go('/settings/prodotti')}>Gestire</button></div>
            </div>
            <div className="nfx-card nfx-org-stat">
              <span className="nfx-org-stat__ic"><Users2 size={20} /></span>
              <div><div className="nfx-org-stat__n">1 Utenti</div><button className="nfx-org-stat__link" onClick={() => go('/account')}>Gestire</button></div>
            </div>
          </div>

          <div className="nfx-card nfx-org-stat">
            <span className="nfx-org-stat__ic"><Tag size={20} /></span>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="nfx-org-stat__n">Etichetta</div>
              <button className="nfx-org-stat__link" onClick={() => go('/settings/etichette')}>Gestire</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 1b. MODIFICA ORGANIZZAZIONE ═══════════════════ */
export const OrganizzazioneModificaPage = () => {
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const [profilo] = useFetch('/api/profilo', null, null);
  const AF0 = { tipo: 'azienda', ragione: '', nome: '', cognome: '', cf: '', piva: '', pivaUe: '', sdi: '', pec: '', prefisso: '+39', telefono: '', indirizzo: '', complemento: '', cap: '', citta: '', provincia: '', nazione: 'IT' };
  const [af, setAf] = useState(AF0);
  const [ready, setReady] = useState(false);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!profilo || ready) return;
    const ind = Array.isArray(profilo.indirizzi) ? profilo.indirizzi : [];
    const a0 = ind.find((x) => x?.tipo === 'fatturazione') || ind.find((x) => x?.tipo === 'principale') || ind[0] || {};
    setAf({
      ...AF0,
      tipo: profilo.tipo_cliente || 'azienda',
      ragione: profilo.ragione_sociale || '', nome: profilo.nome || '', cognome: profilo.cognome || '',
      cf: profilo.codice_fiscale || profilo.codice_fiscale_org || '',
      piva: profilo.partita_iva || '', pivaUe: profilo.partita_iva_comunitaria || '',
      sdi: profilo.codice_sdi || '', pec: profilo.pec || '',
      prefisso: profilo.prefisso_telefono || '+39', telefono: profilo.telefono || '',
      indirizzo: a0.indirizzo || '', complemento: a0.complemento || '', cap: a0.cap || '',
      citta: a0.citta || '', provincia: a0.provincia || '', nazione: a0.nazione || 'IT',
    });
    setReady(true);
  }, [profilo]); // eslint-disable-line
  const ch = (k) => (e) => setAf((s) => ({ ...s, [k]: e.target.value }));
  const fx = fiscalFor(af.nazione, af.tipo);

  const save = async () => {
    setSaving(true); setErr('');
    try { await api.put('/api/fatturazione/indirizzo', af); go('/settings'); }
    catch (e) { setErr(e.response?.data?.messaggio || 'Salvataggio non riuscito.'); setSaving(false); }
  };

  return (
    <div className="nfx-cb">
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => go('')} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__link" onClick={() => go('/settings')}>Parametri</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__link" onClick={() => go('/settings')}>La tua organizzazione</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">Modifica i dati dell'organizzazione</span>
      </div>
      <h1 className="nfx-cb-title">Modifica i dati della mia organizzazione</h1>

      <div className="nfx-card nfx-card--pad nfx-orge">
        <div className="nfx-orge-alert"><Info size={16} /><span>I dati della tua organizzazione vengono utilizzati di default per l'emissione delle fatture.</span></div>
        <form onSubmit={(e) => { e.preventDefault(); if (consent && !saving) save(); }}>
          {err && <div className="nfx-carderr">{err}</div>}

          <div className="nfx-orge-sub">Informazioni</div>
          <div className="nfx-orge-grid">
            <div className="nfx-dlg__field"><label>Tipo di cliente</label>
              <select value={af.tipo} onChange={ch('tipo')}>{TIPO_CLIENTE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
            {fx.fields.ragioneSociale
              ? <div className="nfx-dlg__field"><label>Nome dell'organizzazione *</label><input value={af.ragione} onChange={ch('ragione')} /></div>
              : (<>
                  <div className="nfx-dlg__field"><label>Nome</label><input value={af.nome} onChange={ch('nome')} /></div>
                  <div className="nfx-dlg__field"><label>Cognome</label><input value={af.cognome} onChange={ch('cognome')} /></div>
                </>)}
          </div>

          <div className="nfx-orge-sub">Indirizzo</div>
          <div className="nfx-orge-grid">
            <div className="nfx-dlg__field"><label>Indirizzo postale *</label><input value={af.indirizzo} onChange={ch('indirizzo')} /></div>
            <div className="nfx-dlg__field"><label>Indirizzo aggiuntivo</label><input value={af.complemento} onChange={ch('complemento')} /></div>
            <div className="nfx-dlg__field"><label>Codice postale *</label><input value={af.cap} onChange={ch('cap')} /></div>
            <div className="nfx-dlg__field"><label>Città *</label><input value={af.citta} onChange={ch('citta')} /></div>
            <div className="nfx-dlg__field"><label>Provincia</label><input value={af.provincia} onChange={ch('provincia')} /></div>
            <div className="nfx-dlg__field"><label>Paese</label>
              <select value={af.nazione} onChange={ch('nazione')}>{PAESI.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}</select></div>
          </div>

          <div className="nfx-orge-sub">Telefono</div>
          <div className="nfx-orge-grid">
            <div className="nfx-dlg__field" style={{ flex: '0 0 120px' }}><label>Prefisso</label><input value={af.prefisso} onChange={ch('prefisso')} /></div>
            <div className="nfx-dlg__field"><label>Telefono *</label><input value={af.telefono} onChange={ch('telefono')} /></div>
          </div>

          {(fx.fields.ragioneSociale || fx.fields.codiceFiscale || fx.fields.partitaIva || fx.fields.partitaIvaComunitaria || fx.fields.sdiPec) && (
            <>
              <div className="nfx-orge-sub">Dati fiscali</div>
              <div className="nfx-orge-grid">
                {(fx.fields.codiceFiscale || fx.fields.codiceFiscaleOrg) &&
                  <div className="nfx-dlg__field"><label>Codice fiscale</label><input value={af.cf} onChange={ch('cf')} /></div>}
                {fx.fields.partitaIva &&
                  <div className="nfx-dlg__field"><label>Partita IVA</label><input value={af.piva} onChange={ch('piva')} /></div>}
                {fx.fields.partitaIvaComunitaria &&
                  <div className="nfx-dlg__field"><label>Partita IVA comunitaria</label><input value={af.pivaUe} onChange={ch('pivaUe')} /></div>}
                {fx.fields.sdiPec &&
                  <div className="nfx-dlg__field"><label>Codice SDI</label><input value={af.sdi} onChange={ch('sdi')} /></div>}
                {fx.fields.sdiPec &&
                  <div className="nfx-dlg__field"><label>PEC</label><input value={af.pec} onChange={ch('pec')} /></div>}
              </div>
            </>
          )}

          <label className="nfx-orge-consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>Modificando le informazioni della mia organizzazione, accetto le <a href="#cg" onClick={(e) => e.preventDefault()}>condizioni generali</a></span>
          </label>

          <div className="nfx-orge-actions">
            <button type="button" className="nfx-btn-sec" onClick={() => go('/settings')}>Annullare</button>
            <button type="submit" className="nfx-btn-blue" disabled={!consent || saving}>{saving ? 'Salvataggio…' : 'Registrare'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════ 2. PERSONALIZZAZIONE ═══════════════════ */
export const PersonalizzazionePage = () => {
  const { utente } = useAuth();
  const [profilo] = useFetch('/api/profilo', null, null);
  const initials = initialsOf(profilo?.nome_completo, profilo?.email || utente?.email);
  return (
    <div className="nfx-cb">
      <SettCrumb current="Personalizzazione" />
      <h1 className="nfx-cb-title">Personalizzazione</h1>
      <p className="nfx-cb-intro">Personalizza l'aspetto del tuo Manager: logo, colori e branding visibili a te e ai tuoi utenti.</p>
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-sec__title" style={{ marginBottom: 16 }}>Logo</div>
        <div className="nfx-setting">
          <span className="nfx-org-logobox" style={{ width: 48, height: 48, fontSize: 16 }}>{initials}</span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Logo del manager</div>
            <div className="nfx-setting__desc">Mostrato nell'intestazione del tuo Manager.</div>
          </div>
          <button className="nfx-btn-blue">Carica</button>
        </div>
        <div className="nfx-setting">
          <span className="nfx-org-logobox" style={{ width: 48, height: 48, fontSize: 16 }}>{initials}</span>
          <div className="nfx-setting__body">
            <div className="nfx-setting__name">Logo delle applicazioni</div>
            <div className="nfx-setting__desc">Mostrato nelle applicazioni collegate al tuo account.</div>
          </div>
          <button className="nfx-btn-blue">Carica</button>
        </div>
      </div>
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-org-brand" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
          <span className="nfx-org-brand__ic"><Palette size={20} /></span>
          <span className="nfx-org-brand__t">Custom Brand — personalizza colori e dominio</span>
          <a className="nfx-acc-link" href="#info" onClick={e => e.preventDefault()}>Per più informazioni</a>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 3. GESTIONE DEI PRODOTTI ═══════════════════ */
const PROD_META = {
  vps: { Icon: Cloud, color: '#B39DDB' }, cloud: { Icon: Cloud, color: '#B39DDB' },
  hosting: { Icon: Layers, color: '#26A69A' }, hosting_web: { Icon: Layers, color: '#26A69A' },
  dominio: { Icon: Globe, color: '#1DBAB0' }, domini: { Icon: Globe, color: '#1DBAB0' }, domain: { Icon: Globe, color: '#1DBAB0' },
  pec: { Icon: FileText, color: '#2C76E8' }, dedicato: { Icon: Server, color: '#7986CB' },
};
const prodMeta = (cat) => PROD_META[String(cat || '').toLowerCase()] || { Icon: Package, color: '#9AA4B2' };

const PROD_DASH = {
  vps: '/cloud-dedicated', cloud: '/cloud-dedicated', dedicato: '/cloud-dedicated',
  hosting: '/hosting-domains/hosting', hosting_web: '/hosting-domains/hosting',
  dominio: '/hosting-domains/domains', domini: '/hosting-domains/domains', domain: '/hosting-domains/domains',
  pec: '/pec-trust',
};

const ProdRowMenu = ({ p, open, onToggle, onAction }) => (
  <div className="nfx-rowmenu">
    <button className={`nfx-rowmenu__btn${open ? ' is-open' : ''}`} onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-label="Azioni"><MoreVertical size={16} /></button>
    {open && (
      <div className="nfx-rowmenu__pop nfx-prod-menu" onClick={(e) => e.stopPropagation()}>
        <button className="nfx-rowmenu__item" onClick={() => onAction('dashboard')}><Gauge size={14} /> Accedi alla dashboard</button>
        <button className="nfx-rowmenu__item" onClick={() => onAction('manutenzione')}><Wrench size={14} /> {p.manutenzione ? 'Disattivare la manutenzione' : 'Attivare la manutenzione del prodotto'}</button>
        <button className="nfx-rowmenu__item" onClick={() => onAction('rinomina')}><Pencil size={14} /> Rinomina il prodotto</button>
        <button className="nfx-rowmenu__item" onClick={() => onAction('blocco')}><Ban size={14} /> {p.gestione_bloccata ? 'Sbloccare la gestione' : 'Bloccare la gestione del prodotto'}</button>
        <button className="nfx-rowmenu__item nfx-rowmenu__item--danger" onClick={() => onAction('rescindi')}><Trash2 size={14} /> Rescindere</button>
      </div>
    )}
  </div>
);

export const ProdottiSettingsPage = () => {
  const navigate = useNavigate();
  const [prodotti, setProdotti] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [view, setView] = useState('list');
  const [menu, setMenu] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fsec, setFsec] = useState({ prodotto: true, etichetta: false, scadenza: false });
  const [draft, setDraft] = useState({ types: [], tags: [], scad: null });   // filtri in modifica
  const [filters, setFilters] = useState({ types: [], tags: [], scad: null }); // filtri applicati
  const [rename, setRename] = useState(null);   // { numero, nome }
  const [tagP, setTagP] = useState(null);        // { numero, tags:[] }
  const [rescind, setRescind] = useState(null);  // { numero, nome }
  const [toast, setToast] = useState('');

  const load = React.useCallback(() => {
    api.get('/api/prodotti').then((r) => setProdotti(r.data.prodotti || [])).catch(() => setProdotti([]));
    api.get('/api/prodotti/tags').then((r) => setAllTags(r.data.tags || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => { setMenu(null); setFilterOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 2600); return () => clearTimeout(t); }, [toast]);

  const all = prodotti || [];
  const daysLeft = (s) => (s.data_scadenza ? Math.ceil((new Date(s.data_scadenza).getTime() - Date.now()) / 86400000) : null);
  const scadLabel = (s) => {
    if (!s.data_scadenza) return s.rinnovo_automatico ? 'Rinnovo automatico' : 'Non scade mai';
    const gg = daysLeft(s);
    return `Scade il ${fmtDate(s.data_scadenza)}${gg == null || isNaN(gg) ? '' : ` (G-${gg})`}`;
  };
  const types = [...new Set(all.map((s) => s.categoria).filter(Boolean))];

  const list = all.filter((s) => {
    if (q.trim()) { const t = q.toLowerCase(); if (!(String(s.nome).toLowerCase().includes(t) || String(s.categoria).toLowerCase().includes(t))) return false; }
    if (filters.types.length && !filters.types.includes(s.categoria)) return false;
    if (filters.tags.length && !(s.tags || []).some((x) => filters.tags.includes(x))) return false;
    if (filters.scad) { const gg = daysLeft(s); if (gg == null || gg > filters.scad) return false; }
    return true;
  });
  const nFilters = filters.types.length + filters.tags.length + (filters.scad ? 1 : 0);

  const Ic = ({ s, size = 16 }) => { const m = prodMeta(s.categoria); const I = m.Icon; return <span className="nfx-prod-ic"><I size={size} style={{ color: m.color }} /></span>; };

  const patch = (numero, body, msg) => api.patch(`/api/prodotti/${numero}`, body).then(() => { setToast(msg); load(); }).catch(() => setToast('Operazione non riuscita'));
  const onAction = (p, action) => {
    setMenu(null);
    if (action === 'dashboard') { navigate(V2_BASE + (PROD_DASH[String(p.categoria || '').toLowerCase()] || '/')); return; }
    if (action === 'rinomina') { setRename({ numero: p.numero, nome: p.nome || '' }); return; }
    if (action === 'manutenzione') { patch(p.numero, { manutenzione: !p.manutenzione }, p.manutenzione ? 'Manutenzione disattivata' : 'Manutenzione attivata'); return; }
    if (action === 'blocco') { patch(p.numero, { gestione_bloccata: !p.gestione_bloccata }, p.gestione_bloccata ? 'Gestione sbloccata' : 'Gestione bloccata'); return; }
    if (action === 'rescindi') { setRescind({ numero: p.numero, nome: p.nome }); return; }
  };

  const toggleDraft = (key, val) => setDraft((d) => {
    if (key === 'scad') return { ...d, scad: d.scad === val ? null : val };
    const arr = d[key].includes(val) ? d[key].filter((x) => x !== val) : [...d[key], val];
    return { ...d, [key]: arr };
  });

  const rowMenu = (p, id) => <ProdRowMenu p={p} open={menu === id} onToggle={() => setMenu(menu === id ? null : id)} onAction={(a) => onAction(p, a)} />;
  const tagCell = (p) => (
    (p.tags && p.tags.length)
      ? <span className="nfx-prod-tags" onClick={() => setTagP({ numero: p.numero, tags: [...p.tags] })}>{p.tags.map((t) => <span className="nfx-prod-tag" key={t}>{t}</span>)}</span>
      : <button className="nfx-prod-addtag" title="Aggiungere un tag" onClick={() => setTagP({ numero: p.numero, tags: [] })}><PlusCircle size={18} /></button>
  );

  return (
    <div className="nfx-cb">
      <SettCrumb current="Gestione dei prodotti" />
      <h1 className="nfx-cb-title" style={{ marginBottom: 8 }}>Gestisci i tuoi prodotti</h1>
      <p className="nfx-cb-intro">Gestisci, trasferisci o disattiva i prodotti della tua organizzazione.</p>

      <div className="nfx-prod-bar">
        <span className="nfx-prod-count"><b>{list.length}</b> {list.length === 1 ? 'Prodotto' : 'Prodotti'}</span>
        <span className="nfx-tb__spacer" />
        <button className="nfx-linkbtn" onClick={() => navigate(V2_BASE + '/settings/prodotti')}><ArrowRightLeft size={16} /> Trasferisci i prodotti</button>
        {searchOpen
          ? <label className="nfx-prod-search"><Search size={16} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => { if (!q) setSearchOpen(false); }} placeholder="Ricerca" /></label>
          : <button className="nfx-prod-ibtn" onClick={() => setSearchOpen(true)} title="Ricerca"><Search size={18} /></button>}
        <div className="nfx-prod-fwrap">
          <button className={`nfx-prod-ibtn${filterOpen || nFilters ? ' is-selected' : ''}`} onClick={(e) => { e.stopPropagation(); setDraft(filters); setFilterOpen((v) => !v); }} title={`Filtra (${nFilters})`}><SlidersHorizontal size={18} /></button>
          {filterOpen && (
            <div className="nfx-prod-fp" onClick={(e) => e.stopPropagation()}>
              <div className="nfx-prod-fp__title">Seleziona i filtri da applicare</div>
              <div className={`nfx-prod-fsec${fsec.prodotto ? ' is-open' : ''}`}>
                <button className="nfx-prod-fsec__head" onClick={() => setFsec((f) => ({ ...f, prodotto: !f.prodotto }))}>Prodotto<ChevronRight size={20} /></button>
                <div className="nfx-prod-fsec__body">
                  {types.length ? types.map((c) => <span className={`nfx-prod-fchip${draft.types.includes(c) ? ' is-on' : ''}`} key={c} onClick={() => toggleDraft('types', c)}>{c}<PlusCircle size={18} /></span>) : <span className="nfx-prod-type">Nessun prodotto</span>}
                </div>
              </div>
              <div className={`nfx-prod-fsec${fsec.etichetta ? ' is-open' : ''}`}>
                <button className="nfx-prod-fsec__head" onClick={() => setFsec((f) => ({ ...f, etichetta: !f.etichetta }))}>Etichetta<ChevronRight size={20} /></button>
                <div className="nfx-prod-fsec__body">
                  {allTags.length ? allTags.map((t) => <span className={`nfx-prod-fchip${draft.tags.includes(t) ? ' is-on' : ''}`} key={t} onClick={() => toggleDraft('tags', t)}>{t}<PlusCircle size={18} /></span>) : <span className="nfx-prod-type">Nessuna etichetta</span>}
                </div>
              </div>
              <div className={`nfx-prod-fsec${fsec.scadenza ? ' is-open' : ''}`}>
                <button className="nfx-prod-fsec__head" onClick={() => setFsec((f) => ({ ...f, scadenza: !f.scadenza }))}>Scadenza<ChevronRight size={20} /></button>
                <div className="nfx-prod-fsec__body">
                  {[['Meno di 30 giorni', 30], ['Meno di 90 giorni', 90]].map(([label, v]) => <span className={`nfx-prod-fchip${draft.scad === v ? ' is-on' : ''}`} key={v} onClick={() => toggleDraft('scad', v)}>{label}<PlusCircle size={18} /></span>)}
                </div>
              </div>
              <div className="nfx-prod-fp__foot">
                <button className="nfx-linkbtn" onClick={() => { setDraft({ types: [], tags: [], scad: null }); setFilters({ types: [], tags: [], scad: null }); }}>Re-inizializzare</button>
                <button className="nfx-linkbtn" onClick={() => { setFilters(draft); setFilterOpen(false); }}>Applica filtri</button>
              </div>
            </div>
          )}
        </div>
        {view === 'cards' && <button className="nfx-prod-ibtn" title="Ordina per"><ArrowDownUp size={18} /></button>}
        <button className={`nfx-prod-ibtn${view === 'cards' ? ' is-selected' : ''}`} onClick={() => setView('cards')} title="Vista a schede"><LayoutGrid size={18} /></button>
        <button className={`nfx-prod-ibtn${view === 'list' ? ' is-selected' : ''}`} onClick={() => setView('list')} title="Vista tabella"><List size={18} /></button>
      </div>

      {prodotti === null ? <div className="nfx-card"><div className="nfx-token-empty"><div className="nfx-token-empty__text">Caricamento…</div></div></div>
        : list.length === 0 ? <div className="nfx-card"><div className="nfx-empty2"><span className="nfx-empty2__icon"><Package size={40} /></span><div className="nfx-empty2__title">{all.length ? 'Nessun risultato' : 'Nessun prodotto'}</div></div></div>
        : view === 'list' ? (
          <table className="nfx-prod-table">
            <thead><tr><th /><th>Nome</th><th>Scadenza</th><th>Tag</th><th /></tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.numero}>
                  <td className="nfx-prod-table__ic"><Ic s={p} /></td>
                  <td><div className="nfx-prod-name">{p.nome} {p.manutenzione && <span className="nfx-prod-flag">manutenzione</span>}{p.gestione_bloccata && <span className="nfx-prod-flag nfx-prod-flag--warn">bloccato</span>}</div><div className="nfx-prod-type">{p.categoria}</div></td>
                  <td className="nfx-prod-scad">{scadLabel(p)}{p.rescissione_richiesta && <span className="nfx-prod-flag nfx-prod-flag--warn">rescissione richiesta</span>}</td>
                  <td>{tagCell(p)}</td>
                  <td className="nfx-prod-table__act">{rowMenu(p, p.numero)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="nfx-prod-cards">
            {list.map((p) => (
              <div className="nfx-prod-card" key={p.numero}>
                <div className="nfx-prod-card__body">
                  <Ic s={p} />
                  <div className="nfx-prod-card__txt">
                    <div className="nfx-prod-name">{p.nome}</div>
                    <div className="nfx-prod-type">{p.categoria}</div>
                    <div className="nfx-prod-type" style={{ marginTop: 8 }}>{scadLabel(p)}</div>
                  </div>
                  <div className="nfx-prod-card__more">{rowMenu(p, 'c' + p.numero)}</div>
                </div>
                <div className="nfx-prod-card__foot">{(p.tags || []).map((t) => <span className="nfx-prod-tag" key={t}>{t}</span>)}</div>
              </div>
            ))}
          </div>
        )}

      {rename && <RenameProdModal p={rename} onClose={() => setRename(null)} onSave={(nome) => { patch(rename.numero, { nome }, 'Prodotto rinominato'); setRename(null); }} />}
      {tagP && <TagProdModal p={tagP} allTags={allTags} onClose={() => setTagP(null)} onSave={(tags) => { patch(tagP.numero, { tags }, 'Etichette aggiornate'); setTagP(null); }} />}
      {rescind && (
        <div className="nfx-modal-scrim" onClick={() => setRescind(null)}>
          <div className="nfx-prod-tagmodal" style={{ width: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="nfx-prod-tagmodal__head">Rescindere il prodotto<button className="nfx-prod-tagmodal__x" onClick={() => setRescind(null)}><X size={16} /></button></div>
            <div className="nfx-prod-tagmodal__body">
              <p className="nfx-modal__p">Confermi la richiesta di rescissione di <b>{rescind.nome}</b>? Verrà presa in carico dal nostro team.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
                <button className="nfx-btn-sec" onClick={() => setRescind(null)}>Annullare</button>
                <button className="nfx-acc-btn-danger" onClick={() => { api.post(`/api/prodotti/${rescind.numero}/rescissione`).then(() => { setToast('Richiesta di rescissione inviata'); load(); }).catch(() => setToast('Operazione non riuscita')); setRescind(null); }}>Sì, rescindi</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="nfx-tok-toast"><Check size={16} className="ok" /> <span>{toast}</span><button className="x" onClick={() => setToast('')}><X size={16} /></button></div>}
    </div>
  );
};

/* Modale: rinomina prodotto */
const RenameProdModal = ({ p, onClose, onSave }) => {
  const [nome, setNome] = useState(p.nome || '');
  return (
    <div className="nfx-modal-scrim" onClick={onClose}>
      <div className="nfx-prod-tagmodal" style={{ width: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="nfx-prod-tagmodal__head">Rinomina il prodotto<button className="nfx-prod-tagmodal__x" onClick={onClose}><X size={16} /></button></div>
        <div className="nfx-prod-tagmodal__body">
          <label className="nfx-prod-taginput"><input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome del prodotto" onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) onSave(nome.trim()); }} /></label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <button className="nfx-btn-sec" onClick={onClose}>Annullare</button>
            <button className="nfx-btn-blue" disabled={!nome.trim()} onClick={() => onSave(nome.trim())}>Registrare</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* Modale: etichette (tag) del prodotto */
const TagProdModal = ({ p, allTags, onClose, onSave }) => {
  const [tags, setTags] = useState(p.tags || []);
  const [input, setInput] = useState('');
  const add = (t) => { const v = String(t).trim(); if (v && !tags.includes(v)) setTags([...tags, v]); setInput(''); };
  const sugg = allTags.filter((t) => !tags.includes(t) && t.toLowerCase().includes(input.toLowerCase()));
  return (
    <div className="nfx-modal-scrim" onClick={onClose}>
      <div className="nfx-prod-tagmodal" onClick={(e) => e.stopPropagation()}>
        <div className="nfx-prod-tagmodal__head">Etichetta<button className="nfx-prod-tagmodal__x" onClick={onClose}><X size={16} /></button></div>
        <div className="nfx-prod-tagmodal__body">
          {tags.length > 0 && <div className="nfx-prod-tagchips">{tags.map((t) => <span className="nfx-prod-tagchip" key={t}>{t}<button onClick={() => setTags(tags.filter((x) => x !== t))}><X size={12} /></button></span>)}</div>}
          <label className="nfx-prod-taginput"><input autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder="Seleziona o crea un tag" onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) add(input); }} /></label>
          {input.trim() && (
            <div className="nfx-prod-tagsugg">
              {sugg.map((t) => <div key={t} onClick={() => add(t)}><span className="nfx-prod-dot" />{t}</div>)}
              {!allTags.includes(input.trim()) && <div onClick={() => add(input)}><PlusCircle size={14} /> Crea «{input.trim()}»</div>}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <button className="nfx-btn-sec" onClick={onClose}>Annullare</button>
            <button className="nfx-btn-blue" onClick={() => onSave(tags)}>Registrare</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 4. SICUREZZA (org) ═══════════════════ */
export const SicurezzaImpPage = () => {
  const [opts, setOpts] = useState({ due_fasi: false, blocco_sms: false });
  const Card = ({ illu, title, badge, desc, k }) => (
    <div className="nfx-card">
      <div className="nfx-secbig">
        <div className="nfx-secbig__illu">{illu}</div>
        <div className="nfx-secbig__body">
          <div className="nfx-secbig__title">{title} {badge && <span className="nfx-badge-consigliato">{badge}</span>}</div>
          <div className="nfx-secbig__desc">{desc}</div>
        </div>
        <div className="nfx-secbig__ctrl">
          <span className="nfx-secbig__ctrl-label">{opts[k] ? 'Attivato' : 'Disattivato'}</span>
          <Toggle on={opts[k]} onClick={() => setOpts(o => ({ ...o, [k]: !o[k] }))} />
        </div>
      </div>
    </div>
  );
  return (
    <div className="nfx-cb">
      <SettCrumb current="Sicurezza" />
      <h1 className="nfx-cb-title">Sicurezza</h1>
      <p className="nfx-cb-intro">Attiva delle opzioni di sicurezza aggiuntive per tutti gli utenti dell'organizzazione:</p>
      <Card k="due_fasi" illu={<ShieldCheck size={72} strokeWidth={1.1} />}
        title="Verifica in due passaggi obbligatoria (2FA)" badge="Consigliato"
        desc={<>Richiedi una verifica aggiuntiva dell'accesso a tutti gli utenti dell'organizzazione per rafforzare la sicurezza. <a href="#i" onClick={e => e.preventDefault()}>Per più informazioni</a></>} />
      <Card k="blocco_sms" illu={<Lock size={72} strokeWidth={1.1} />}
        title="Blocco del recupero della password tramite SMS"
        desc="Impedisci agli utenti della tua organizzazione di utilizzare il metodo SMS per recuperare la password." />
    </div>
  );
};

/* ═══════════════════ 5-8. Etichetta / Certificati / Condizioni / Contratti ═══════════════════ */
const TAG_COLORS = ['#30ABFF', '#ED2C6E', '#FFB11B', '#029688', '#7974B4', '#3CB572', '#05C2E7', '#D9283A', '#3990BB'];

const TagEditModal = ({ data, onClose, onSave }) => {
  const [nome, setNome] = useState(data.nome || '');
  const [colore, setColore] = useState(data.colore || TAG_COLORS[0]);
  const [pop, setPop] = useState(false);
  return (
    <div className="nfx-modal-scrim" onClick={onClose}>
      <div className="nfx-prod-tagmodal" style={{ width: 340 }} onClick={(e) => e.stopPropagation()}>
        <div className="nfx-prod-tagmodal__head">{data.mode === 'edit' ? 'Modifica il tag' : 'Crea un tag'}<button className="nfx-prod-tagmodal__x" onClick={onClose}><X size={16} /></button></div>
        <div className="nfx-prod-tagmodal__body">
          <div className="nfx-cp-row">
            <div className="nfx-cp">
              <button type="button" className="nfx-cp__btn" onClick={() => setPop((v) => !v)}><span className="nfx-cp__dot" style={{ background: colore }} /><ChevronDown size={12} /></button>
              {pop && <div className="nfx-cp__pop">{TAG_COLORS.map((c) => <span key={c} style={{ background: c }} onClick={() => { setColore(c); setPop(false); }} />)}</div>}
            </div>
            <input className="nfx-cp__name" autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome del tag" onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) onSave(nome.trim(), colore); }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24 }}>
            <button className="nfx-btn-sec" onClick={onClose}>Annullare</button>
            <button className="nfx-btn-blue" disabled={!nome.trim()} onClick={() => onSave(nome.trim(), colore)}>{data.mode === 'edit' ? 'Registrare' : 'Crea'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EtichettaPage = () => {
  const [tags, setTags] = useState(null);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [view, setView] = useState('list');
  const [menu, setMenu] = useState(null);
  const [modal, setModal] = useState(null);   // { mode, nome, colore }
  const [del, setDel] = useState(null);        // { nome }
  const [toast, setToast] = useState('');

  const load = React.useCallback(() => api.get('/api/etichette').then((r) => setTags(r.data.etichette || [])).catch(() => setTags([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const h = () => setMenu(null); document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 2400); return () => clearTimeout(t); }, [toast]);

  const all = tags || [];
  const list = all.filter((t) => !q.trim() || t.nome.toLowerCase().includes(q.toLowerCase()));
  const UseIcons = ({ usage }) => {
    const cats = [...new Set(usage || [])];
    if (!cats.length) return <span className="nfx-prod-type">Non utilizzato</span>;
    return <span className="nfx-etag-use">{cats.slice(0, 8).map((c, i) => { const m = prodMeta(c); const I = m.Icon; return <span key={i} title={c}><I size={14} style={{ color: m.color }} /></span>; })}</span>;
  };
  const saveTag = (nome, colore, orig) => {
    const p = orig ? api.patch(`/api/etichette/${encodeURIComponent(orig)}`, { nome, colore }) : api.post('/api/etichette', { nome, colore });
    p.then(() => { setToast(orig ? 'Tag aggiornato' : 'Tag creato'); setModal(null); load(); }).catch(() => setToast('Operazione non riuscita'));
  };
  const removeTag = (nome) => api.delete(`/api/etichette/${encodeURIComponent(nome)}`).then(() => { setToast('Tag eliminato'); setDel(null); load(); }).catch(() => setToast('Operazione non riuscita'));

  const rowMenu = (t, id) => (
    <div className="nfx-rowmenu">
      <button className={`nfx-rowmenu__btn${menu === id ? ' is-open' : ''}`} onClick={(e) => { e.stopPropagation(); setMenu(menu === id ? null : id); }} aria-label="Azioni"><MoreVertical size={16} /></button>
      {menu === id && (
        <div className="nfx-rowmenu__pop" onClick={(e) => e.stopPropagation()}>
          <button className="nfx-rowmenu__item" onClick={() => { setMenu(null); setModal({ mode: 'edit', nome: t.nome, colore: t.colore }); }}><Pencil size={14} /> Modificare</button>
          <button className="nfx-rowmenu__item nfx-rowmenu__item--danger" onClick={() => { setMenu(null); setDel({ nome: t.nome }); }}><Trash2 size={14} /> Eliminare</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="nfx-cb">
      <SettCrumb current="Etichetta" />
      <h1 className="nfx-cb-title" style={{ marginBottom: 8 }}>Organizza i tuoi prodotti con i tag</h1>
      <p className="nfx-cb-intro">Gestisci i tag utilizzati dalla tua organizzazione su questa pagina.</p>

      <div className="nfx-prod-bar">
        <span className="nfx-prod-count"><b>{list.length}</b> {list.length === 1 ? 'Tag' : 'Tag'}</span>
        <button className="nfx-btn-blue" onClick={() => setModal({ mode: 'create', nome: '', colore: TAG_COLORS[0] })}><Plus size={16} /> Aggiungere un tag</button>
        <span className="nfx-tb__spacer" />
        {searchOpen
          ? <label className="nfx-prod-search"><Search size={16} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => { if (!q) setSearchOpen(false); }} placeholder="Ricerca" /></label>
          : <button className="nfx-prod-ibtn" onClick={() => setSearchOpen(true)} title="Ricerca"><Search size={18} /></button>}
        <button className={`nfx-prod-ibtn${view === 'cards' ? ' is-selected' : ''}`} onClick={() => setView('cards')} title="Vista a schede"><LayoutGrid size={18} /></button>
        <button className={`nfx-prod-ibtn${view === 'list' ? ' is-selected' : ''}`} onClick={() => setView('list')} title="Vista tabella"><List size={18} /></button>
      </div>

      {tags === null ? <div className="nfx-card"><div className="nfx-token-empty"><div className="nfx-token-empty__text">Caricamento…</div></div></div>
        : list.length === 0 ? <div className="nfx-card"><div className="nfx-empty2"><span className="nfx-empty2__icon"><Tag size={40} /></span><div className="nfx-empty2__title">{all.length ? 'Nessun risultato' : 'Nessuna etichetta'}</div><div className="nfx-empty2__sub">Crea un'etichetta per organizzare e filtrare i tuoi prodotti.</div></div></div>
        : view === 'list' ? (
          <table className="nfx-etag-table">
            <thead><tr><th>Nome</th><th>Utilizzo</th><th /></tr></thead>
            <tbody>{list.map((t) => (
              <tr key={t.nome}>
                <td><span className="nfx-etag-pill" style={{ background: t.colore }}>{t.nome}</span></td>
                <td><UseIcons usage={t.usage} /></td>
                <td className="nfx-etag-act">{rowMenu(t, t.nome)}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <div className="nfx-etag-cards">{list.map((t) => (
            <div className="nfx-etag-card" key={t.nome}>
              <div className="nfx-etag-card__top"><span className="nfx-etag-pill" style={{ background: t.colore }}>{t.nome}</span>{rowMenu(t, 'c' + t.nome)}</div>
              <div className="nfx-etag-card__foot"><UseIcons usage={t.usage} /></div>
            </div>
          ))}</div>
        )}

      {modal && <TagEditModal data={modal} onClose={() => setModal(null)} onSave={(nome, colore) => saveTag(nome, colore, modal.mode === 'edit' ? modal.nome : null)} />}
      {del && (
        <div className="nfx-modal-scrim" onClick={() => setDel(null)}>
          <div className="nfx-prod-tagmodal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="nfx-prod-tagmodal__head">Conferma<button className="nfx-prod-tagmodal__x" onClick={() => setDel(null)}><X size={16} /></button></div>
            <div className="nfx-prod-tagmodal__body">
              <p className="nfx-modal__p">Eliminare veramente il tag <b>{del.nome}</b>? Verrà rimosso da tutti i prodotti a cui è associato.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
                <button className="nfx-btn-sec" onClick={() => setDel(null)}>Annullare</button>
                <button className="nfx-acc-btn-danger" onClick={() => removeTag(del.nome)}>Sì, elimina</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="nfx-tok-toast"><Check size={16} className="ok" /> <span>{toast}</span><button className="x" onClick={() => setToast('')}><X size={16} /></button></div>}
    </div>
  );
};
const genBody = (nome) => [
  { h: 'Articolo 1 — Ambito di applicazione', p: [
    `Le presenti condizioni disciplinano l'utilizzo del servizio «${nome}» fornito da NeoFlux e completano le Condizioni Generali di Utilizzo, che restano applicabili per quanto non diversamente previsto.`,
    'I termini con iniziale maiuscola hanno il significato loro attribuito nelle Condizioni Generali di Utilizzo di NeoFlux.'] },
  { h: 'Articolo 2 — Descrizione del servizio', p: [
    `NeoFlux mette a disposizione del Cliente il servizio «${nome}» secondo le caratteristiche e i limiti del piano sottoscritto.`,
    'NeoFlux può far evolvere le funzionalità del servizio per ragioni tecniche, di sicurezza o normative, informandone il Cliente quando la modifica è sostanziale.'] },
  { h: 'Articolo 3 — Durata e cessazione', p: [
    "Il presente accordo ha effetto dalla data di attivazione del servizio e resta valido per tutta la durata dell'abbonamento.",
    "Il Cliente può cessare l'utilizzo del servizio secondo le modalità di disdetta previste dalle Condizioni Generali."] },
  { h: 'Articolo 4 — Obblighi del Cliente', p: [
    "Il Cliente utilizza il servizio nel rispetto delle leggi vigenti e dei diritti dei terzi ed è l'unico responsabile dei contenuti trattati tramite il servizio."] },
  { h: 'Articolo 5 — Responsabilità e dati personali', p: [
    'NeoFlux tratta i dati personali del Cliente conformemente alla propria informativa privacy e al Regolamento (UE) 2016/679 (GDPR).'] },
];
const LEGAL_DOCS = [
  { cat: 'Condizioni generali', nome: 'Condizioni Generali di Utilizzo' },
  { cat: 'Condizioni generali', nome: 'Condizioni Generali di Vendita' },
  { cat: 'Hosting', nome: 'Hosting Web' },
  { cat: 'Hosting', nome: 'Applicazioni Web' },
  { cat: 'Hosting', nome: 'Il mio sito WordPress' },
  { cat: 'Hosting', nome: 'Server Cloud gestito' },
  { cat: 'Cloud', nome: 'VPS Cloud' },
  { cat: 'Domini', nome: 'Nomi di dominio' },
  { cat: 'PEC & Trust', nome: 'Posta Elettronica Certificata' },
].map((d) => ({ ...d, body: genBody(d.nome) }));

export const CondizioniPage = () => {
  const [sel, setSel] = useState(0);
  const doc = LEGAL_DOCS[sel];
  const cats = [...new Set(LEGAL_DOCS.map((d) => d.cat))];
  const download = (d) => {
    const text = `${d.nome}\n\n` + d.body.map((a) => `${a.h}\n${a.p.join('\n\n')}`).join('\n\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `${d.nome}.txt`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="nfx-cb">
      <SettCrumb current="Condizioni generali" />
      <h1 className="nfx-cb-title" style={{ marginBottom: 10 }}>Condizioni generali</h1>
      <p className="nfx-cb-intro">Le Condizioni Generali e Speciali regolano l'utilizzo dei servizi NeoFlux.</p>
      <section className="nfx-legal">
        <div className="nfx-legal__head">Documenti legali</div>
        <div className="nfx-legal__body">
          <nav className="nfx-legal__list">
            <div className="nfx-legal__list-title">Documenti in vigore</div>
            {cats.map((cat) => (
              <React.Fragment key={cat}>
                <div className="nfx-legal__cat">{cat}</div>
                {LEGAL_DOCS.map((d, i) => d.cat === cat ? (
                  <button key={d.nome} className={`nfx-legal__item${i === sel ? ' is-active' : ''}`} onClick={() => setSel(i)}>
                    <span className="nfx-legal__name">{d.nome}</span><ChevronRight size={12} />
                  </button>
                ) : null)}
              </React.Fragment>
            ))}
          </nav>
          <div className="nfx-legal__doc">
            <div className="nfx-legal__doc-head">
              <span className="nfx-legal__doc-name">{doc.nome}</span>
              <button className="nfx-legal__dl" title="Scarica" onClick={() => download(doc)}><Download size={16} /></button>
            </div>
            <div className="nfx-legal__text">
              {doc.body.map((a, i) => (
                <React.Fragment key={i}>
                  <h2>{a.h}</h2>
                  {a.p.map((p, j) => <p key={j}>{p}</p>)}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export const ContrattiPage = () => {
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [view, setView] = useState('list');
  useEffect(() => { api.get('/api/contratti-speciali').then((r) => setDocs(r.data.documenti || [])).catch(() => setDocs([])); }, []);
  const all = docs || [];
  const list = all.filter((d) => !q.trim() || `${d.nome} ${d.tipo}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="nfx-cb">
      <SettCrumb current="Documenti e Contratti speciali" />
      <h1 className="nfx-cb-title" style={{ marginBottom: 24 }}>Documenti e Contratti speciali</h1>
      <div className="nfx-prod-bar">
        <span className="nfx-prod-count"><b>{list.length}</b> {list.length === 1 ? 'Documento' : 'Documenti'}</span>
        <span className="nfx-tb__spacer" />
        {searchOpen
          ? <label className="nfx-prod-search"><Search size={16} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => { if (!q) setSearchOpen(false); }} placeholder="Ricerca" /></label>
          : <button className="nfx-prod-ibtn" onClick={() => setSearchOpen(true)} title="Ricerca"><Search size={18} /></button>}
        <button className={`nfx-prod-ibtn${view === 'cards' ? ' is-selected' : ''}`} onClick={() => setView('cards')} title="Vista a schede"><LayoutGrid size={18} /></button>
        <button className={`nfx-prod-ibtn${view === 'list' ? ' is-selected' : ''}`} onClick={() => setView('list')} title="Vista tabella"><List size={18} /></button>
      </div>
      {docs === null ? <div className="nfx-card"><div className="nfx-token-empty"><div className="nfx-token-empty__text">Caricamento…</div></div></div>
        : list.length === 0 ? <div className="nfx-card"><div className="nfx-empty2"><span className="nfx-empty2__icon"><Search size={40} /></span><div className="nfx-empty2__title">Nessun risultato</div><div className="nfx-empty2__sub">Non hai documenti o contratti speciali.</div></div></div>
        : view === 'list' ? (
          <table className="nfx-etag-table">
            <thead><tr><th>Nome</th><th>Tipo</th><th>Data</th><th /></tr></thead>
            <tbody>{list.map((d, i) => (
              <tr key={i}>
                <td><span className="nfx-prod-name">{d.nome}</span></td>
                <td>{d.tipo}</td>
                <td>{d.data_documento ? fmtDate(d.data_documento) : '—'}</td>
                <td className="nfx-etag-act">{d.file_url ? <a className="nfx-legal__dl" href={d.file_url} target="_blank" rel="noopener" title="Scarica"><Download size={16} /></a> : <span className="nfx-prod-type">—</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <div className="nfx-prod-cards">{list.map((d, i) => (
            <div className="nfx-prod-card" key={i}>
              <div className="nfx-prod-card__body">
                <span className="nfx-prod-ic"><FileSignature size={16} style={{ color: '#7986CB' }} /></span>
                <div className="nfx-prod-card__txt">
                  <div className="nfx-prod-name">{d.nome}</div>
                  <div className="nfx-prod-type">{d.tipo}</div>
                  <div className="nfx-prod-type" style={{ marginTop: 8 }}>{d.data_documento ? fmtDate(d.data_documento) : '—'}</div>
                </div>
                {d.file_url && <a className="nfx-prod-card__more nfx-legal__dl" href={d.file_url} target="_blank" rel="noopener"><Download size={16} /></a>}
              </div>
              <div className="nfx-prod-card__foot" />
            </div>
          ))}</div>
        )}
    </div>
  );
};
